'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

const CART_KEY = 'athena_cart';
const CART_BACKUP_PREFIX = 'athena_cart_backup_';

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const safeParse = (v, f) => {
    try { return JSON.parse(v) ?? f; } catch { return f; }
  };

  const backupCartForUser = (userId) => {
    if (!userId) return;
    const cart = safeParse(localStorage.getItem(CART_KEY), []);
    localStorage.setItem(`${CART_BACKUP_PREFIX}${userId}`, JSON.stringify(cart));
  };

  const restoreCartForUser = (userId) => {
    if (!userId) return;
    const backup = safeParse(
      localStorage.getItem(`${CART_BACKUP_PREFIX}${userId}`),
      null
    );
    if (Array.isArray(backup)) {
      localStorage.setItem(CART_KEY, JSON.stringify(backup));
      window.dispatchEvent(new Event('cartUpdated'));
    }
  };

  const clearCartNow = () => {
    localStorage.setItem(CART_KEY, JSON.stringify([]));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          //console.log('No profile found - Normal user');
          setUserProfile(null);
          setIsAdmin(false);
        } else {
          //console.log(' Profile Loaded:', data);
          setUserProfile(data);
          setIsAdmin(data?.role === 'admin');
        }
      } catch (err) {
        //console.log('ℹProfile load failed - Normal user');
        setUserProfile(null);
        setIsAdmin(false);
      } finally {
        setProfileLoading(false);
      }
    },
    clear() {
      setUserProfile(null);
      setIsAdmin(false);
      setProfileLoading(false);
    },
  };

//PERFECT FIX - LOADING NEVER STUCK
const handleAuthChange = async (event, session) => {
  //console.log('🔔 Auth event:', event);
  const authUser = session?.user ?? null;

  //IMMEDIATE USER UPDATE
  setUser(authUser);
  
  if (authUser?.id) {
    //BACKGROUND PROFILE LOAD - NO BLOCKING
    loadProfile(authUser.id).catch(err => {
      console.error('Profile error:', err);
      setProfileLoading(false);
    });
    restoreCartForUser(authUser.id);
  } else {
    setUserProfile(null);
    setIsAdmin(false);
    setProfileLoading(false);
  }
  
  //LOADING ALWAYS FALSE AFTER 2s MAX
  setTimeout(() => {
    setLoading(false);
    setProfileLoading(false);
  }, 2000);
};

//NON-BLOCKING PROFILE LOAD
const loadProfile = async (userId) => {
  if (!userId) {
    setProfileLoading(false);
    return;
  }
  
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, role')
      .eq('id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      // Old user - default role
      setUserProfile({ id: userId, role: 'user' });
      setIsAdmin(false);
    } else if (data) {
      setUserProfile(data);
      setIsAdmin(data.role === 'admin');
    }
  } catch (err) {
    //console.error('Profile load failed:', err);
    setIsAdmin(false);
  } finally {
    //NEVER STUCK
    setProfileLoading(false);
  }
};


  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const { data } = await supabase.auth.getSession();
      await handleAuthChange('INIT', data.session);
    };

    init();
    const { data: listener } = supabase.auth.onAuthStateChange(handleAuthChange);

    return () => listener.subscription.unsubscribe();
  }, []);

  //FIXED: NO ADMIN BLOCKING IN LOGIN!
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (error) {
        //console.error('SignIn Error:', error);
        return { data: null, error };
      }

      //console.log('LOGIN SUCCESS:', data.user.id, data.user.email);
      
      // Profile load (NO BLOCKING!)
      await profileOperations.load(data.user.id);
      
      return { data, error: null }; //ALWAYS SUCCESS!
    } catch (err) {
      //console.error('SignIn Exception:', err);
      return { data: null, error: { message: err.message } };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });

    if (!error && data?.user) {
      await supabase.from('user_profiles').upsert({
        id: data.user.id,
        email: data.user.email,
        role: 'user',
        full_name: metadata.fullName ?? null,
      });
    }

    return { data, error };
  };

  const signOut = async () => {
    if (user?.id) backupCartForUser(user.id);
    clearCartNow();
    await supabase.auth.signOut();
    setUser(null);
    profileOperations.clear();
    return { error: null };
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    isAdmin,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
